/**
 * English is the reference dictionary. Every key must exist here; `he.ts` is
 * typed against it and may be partial, falling back to English per key.
 *
 * Placeholders use {name} and are substituted by t(). Count-sensitive strings
 * use paired _one / _other keys selected by the `count` param.
 */
export const en = {
  // ---- app shell -------------------------------------------------------
  "app.name": "DUNGEON BUILDER",
  "app.tagline": "Dungeon & hex map workshop",
  "app.mapTitleLabel": "Map title",
  "app.save.loading": "Loading…",
  "app.save.ready": "Ready",
  "app.save.saved": "Saved",
  "app.save.error": "Not saved",

  // ---- header actions --------------------------------------------------
  "header.undo": "Undo",
  "header.undoTitle": "Undo · Ctrl/⌘ Z",
  "header.redo": "Redo",
  "header.redoTitle": "Redo · Ctrl/⌘ Shift Z",
  "header.library": "Maps",
  "header.libraryLabel": "Map library",
  "header.import": "Import",
  "header.json": "JSON",
  "header.png": "PNG",
  "header.runMap": "Run map",
  "header.printPdf": "Print PDF",
  "header.fitMap": "Fit map to screen",
  "header.fitMapTitle": "Fit map · 0",
  "header.appearance": "Map appearance",
  "header.showControls": "Show controls",

  // ---- footer ----------------------------------------------------------
  "footer.installable": "Installable · local-first map data",
  "footer.newMap": "New map",
  "footer.history": "History: {count}/50",

  // ---- toolbar ---------------------------------------------------------
  "toolbar.label": "Map tools",
  "tool.select": "Select",
  "tool.pan": "Pan",
  "tool.measure": "Measure",
  "tool.room": "Room",
  "tool.ellipse": "Round",
  "tool.hexagon": "Hex room",
  "tool.corridor": "Path",
  "tool.polygon": "Free",
  "tool.erase": "Erase",
  "tool.terrain": "Terrain",
  "tool.label": "Label",
  "tool.stamp": "Objects",
  "tool.aria": "{label} tool ({key})",
  "tool.title": "{label} · {key}",

  // ---- tool hints ------------------------------------------------------
  "hint.select": "Click an object to select · drag to move",
  "hint.pan": "Drag to move the map",
  "hint.room": "Drag a rectangular room",
  "hint.ellipse": "Drag an ellipse or circle",
  "hint.hexagon": "Drag a six-sided room",
  "hint.corridor": "Drag between two points",
  "hint.polygon": "Click corners · click the first point or press Enter to close",
  "hint.erase": "Drag to remove floor geometry",
  "hint.stamp": "Choose an asset, then click the map",
  "hint.terrain": "Choose terrain, then click or drag across hexes",
  "hint.label": "Type a label or room number, then click the map",
  "hint.measure": "Drag between two points to measure distance",
  "hint.selectedObject": "Drag object to move · corners resize · red handle rotates",
  "hint.eraseBrush": "Erase brush · click or drag to remove hexes",

  // ---- status bar ------------------------------------------------------
  "status.dungeon": "DUNGEON",
  "status.hexcrawl": "HEXCRAWL",
  "status.regions": "{count} regions",
  "status.regions_one": "{count} region",
  "status.regions_other": "{count} regions",
  "status.hexes": "{count} hexes",
  "status.hexes_one": "{count} hex",
  "status.hexes_other": "{count} hexes",
  "status.objects": "{count} objects",
  "status.objects_one": "{count} object",
  "status.objects_other": "{count} objects",

  // ---- empty state -----------------------------------------------------
  "empty.hexTitle": "Paint your first hex",
  "empty.hexBody": "Choose a terrain type, then click or drag across the hex grid.",
  "empty.dungeonTitle": "Carve your first room",
  "empty.dungeonBody": "Choose any room shape and drag across the grid. Overlaps merge cleanly.",

  // ---- help panel ------------------------------------------------------
  "help.title": "Quick controls",
  "help.subtitle": "Build, furnish and print your dungeon.",
  "help.close": "Close help",
  "help.roomsLabel": "Rooms:",
  "help.roomsBody": "rectangle, ellipse and hexagon tools merge automatically.",
  "help.modesLabel": "Map modes:",
  "help.modesBody": "Dungeon and Hexcrawl are separate canvases. Switching keeps the inactive canvas safely hidden.",
  "help.hexLabel": "Hexcrawl:",
  "help.hexBody": "choose Terrain to paint. Choose the prominent Erase brush, press E, or right-click and drag to remove individual hexes.",
  "help.pathLabel": "Path:",
  "help.pathBody": "drag a one-cell-wide corridor in any direction.",
  "help.freeLabel": "Free:",
  "help.freeBody": "click each corner; click the first point or press Enter to finish.",
  "help.stampsLabel": "Stamps:",
  "help.stampsBody": "place portals, furniture and props. Doors snap and rotate to nearby walls.",
  "help.objectsLabel": "Objects:",
  "help.objectsBody": "every placed object is selected automatically. Drag it to move, drag corners to resize, or use the red handle to rotate.",
  "help.labelsLabel": "Labels:",
  "help.labelsBody": "add free text and numbered room markers; use More on a selected label to edit its content and style.",
  "help.roomKeysLabel": "Room keys:",
  "help.roomKeysBody": "double-click a room number with the Select tool to write its description, read-aloud text, senses and DM notes.",
  "help.runLabel": "Run map:",
  "help.runBody": "opens a distraction-free view for moving hero/enemy tokens and measuring distances.",
  "help.moveLabel": "Move:",
  "help.moveBody": "Pan, middle-mouse, Space + drag, or two fingers.",
  "help.keyWheel": "Wheel · Zoom",
  "help.keyFit": "0 · Fit map",
  "help.keyRoom": "R · Room",
  "help.keyTerrain": "T · Terrain",
  "help.keyLabel": "L · Label",
  "help.keyMeasure": "M · Measure",
  "help.keyObjects": "S · Objects",
  "help.keyUndo": "⌘Z · Undo",

  // ---- run mode --------------------------------------------------------
  "run.badge": "Run mode",
  "run.controls": "Run map controls",
  "run.moveTokens": "Move tokens",
  "run.panMap": "Pan map",
  "run.measure": "Measure distance",
  "run.fitMap": "Fit map",
  "run.exitLabel": "Exit run mode",
  "run.exit": "Exit",

  // ---- settings panel --------------------------------------------------
  "settings.title": "Map settings",
  "settings.subtitle": "Mode, grid, ink and paper.",
  "settings.close": "Close appearance settings",
  "settings.mapMode": "Map mode",
  "settings.dungeon": "Dungeon",
  "settings.hexcrawl": "Hexcrawl",
  "settings.modeSummaryDungeon": "{regions} regions · {objects} objects",
  "settings.modeSummaryHex": "{hexes} hexes · {objects} objects",
  "settings.modeNote": "Dungeon and Hexcrawl are separate canvases in the same project. Switching hides the other canvas without deleting it.",
  "settings.palette": "Palette",
  "settings.paper": "Paper",
  "settings.floor": "Floor",
  "settings.walls": "Walls",
  "settings.hatching": "Hatching",
  "settings.colorFor": "{label} color",
  "settings.drawing": "Drawing",
  "settings.hexSize": "Hex size",
  "settings.cellSize": "Cell size",
  "settings.px": "{value} px",
  "settings.wallWeight": "Wall weight",
  "settings.gridOn": "Grid on",
  "settings.gridOff": "Grid off",
  "settings.hatchingOn": "Hatching on",
  "settings.hatchingOff": "Hatching off",
  "settings.coordsOn": "Coords on",
  "settings.coordsOff": "Coords off",
  "settings.clearConfirm": "Clear every {mode} element on this canvas? You can still use Undo immediately afterward.",
  "settings.clearCanvas": "Clear this {mode} canvas",
  "settings.language": "Language",
  "settings.languageEnglish": "English",
  "settings.languageHebrew": "עברית",

  // ---- stamp drawer ----------------------------------------------------
  "stamps.title": "Objects & tokens",
  "stamps.subtitle": "Choose an item and click the map. It becomes selected so you can drag it immediately.",
  "stamps.keepPlacing": "Keep placing",
  "stamps.keepPlacingBody": "Stay on the object tool after each click",
  "category.Architecture": "Architecture",
  "category.Furniture": "Furniture",
  "category.Props": "Props",
  "category.Tokens": "Tokens",
  "category.Annotations": "Annotations",

  // ---- stamp catalogue -------------------------------------------------
  "stamp.door": "Door",
  "stamp.portal": "Portal",
  "stamp.stairs": "Stairs",
  "stamp.table": "Table",
  "stamp.bed": "Bed",
  "stamp.rug": "Rug",
  "stamp.chest_wood": "Wood Chest",
  "stamp.chest_iron": "Iron Chest",
  "stamp.chest_gold": "Gold Chest",
  "stamp.crate": "Crate",
  "stamp.marker": "Markers",
  "stamp.pillar": "Pillar",
  "stamp.brazier": "Brazier",
  "stamp.statue": "Statue",
  "stamp.token_player": "Hero",
  "stamp.token_enemy": "Enemy",
  "stamp.token_npc": "NPC",
  "stamp.text_label": "Text label",
  "stamp.room_number": "Room number",

  // ---- wall objects (door & passage family) ----------------------------
  "wall.single": "Door",
  "wall.double": "Double door",
  "wall.locked": "Locked door",
  "wall.stuck": "Stuck / barred door",
  "wall.secret": "Secret door",
  "wall.concealed": "Concealed door",
  "wall.portcullis": "Portcullis",
  "wall.bars": "Fixed bars",
  "wall.archway": "Archway",
  "wall.curtain": "Curtain",
  "wall.one_way": "One-way door",
  "wall.window": "Window",
  "wall.arrow_slit": "Arrow slit",
  "wallCategory.doors": "Doors",
  "wallCategory.hidden": "Hidden",
  "wallCategory.barriers": "Barriers",
  "wallCategory.openings": "Openings",
  "wall.pickerTitle": "Door & passage type",
  "wall.pickerHint": "Press D to cycle types while the Objects tool is active.",
  "wall.dmOnly": "DM only",
  "wall.dmOnlyHint": "Shown to you, hidden from players in Phase 5 player view.",

  // ---- floor objects (vertical movement family) ------------------------
  "floor.stairs_up": "Stairs up",
  "floor.stairs_down": "Stairs down",
  "floor.spiral": "Spiral staircase",
  "floor.ladder": "Ladder",
  "floor.trapdoor_floor": "Trapdoor (floor)",
  "floor.trapdoor_ceiling": "Trapdoor (ceiling)",
  "floor.pit_open": "Open pit",
  "floor.pit_covered": "Covered pit",
  "floor.pit_spiked": "Spiked pit",
  "floor.shaft": "Shaft / well",
  "floor.ramp": "Ramp",
  "floorCategory.stairs": "Stairs & slopes",
  "floorCategory.access": "Access",
  "floorCategory.hatches": "Hatches",
  "floorCategory.hazards": "Hazards",
  "floor.pickerTitle": "Vertical movement",
  "floor.pickerHint": "Press R to rotate — before placing, or on the selected object.",
  "floor.rotation": "Rotation: {degrees}°",

  // ---- markers & furniture ---------------------------------------------
  "marker.trap": "Trap",
  "marker.pillar_round": "Round pillar",
  "marker.pillar_square": "Square pillar",
  "marker.statue": "Statue",
  "marker.altar": "Altar",
  "marker.dais": "Dais",
  "marker.throne": "Throne",
  "marker.brazier": "Brazier",
  "marker.chest_wood": "Wood chest",
  "marker.chest_iron": "Iron chest",
  "marker.chest_gold": "Gold chest",
  "markerCategory.markers": "Markers",
  "markerCategory.architecture": "Architecture",
  "markerCategory.furnishings": "Furnishings",
  "marker.pickerTitle": "Markers & furniture",
  "marker.pickerHint": "Press R to rotate — before placing, or on the selected object.",

  // ---- terrain patches --------------------------------------------------
  "patch.rubble": "Rubble",
  "patch.shallow_water": "Shallow water",
  "patch.deep_water": "Deep water",
  "patch.chasm": "Chasm",
  "patch.title": "Terrain patches",
  "patch.subtitle": "Drag a region, or build one corner by corner. Patches sit on the floor, under objects.",
  "patch.shapeRect": "Rectangle",
  "patch.shapeEllipse": "Ellipse",
  "patch.shapePolygon": "Polygon",
  "patch.shape": "Shape",
  "patch.erase": "Erase patch",
  "patch.eraseActive": "Erase brush active",
  "patch.count_one": "{count} patch",
  "patch.count_other": "{count} patches",
  "patch.count": "{count} patches",
  "tool.patch": "Terrain",
  "hint.patch": "Drag a terrain region · switch shape in the panel",
  "hint.patchErase": "Click a patch to remove it",

  // ---- terrain drawer --------------------------------------------------
  "terrain.title": "Hexcrawl terrain",
  "terrain.subtitle": "Paint with left-click. Right-click always erases.",
  "terrain.eraseActive": "Erase brush active",
  "terrain.erase": "Erase terrain",
  "terrain.paintedHexes": "{count} painted hexes",
  "terrain.paintedHexes_one": "{count} painted hex",
  "terrain.paintedHexes_other": "{count} painted hexes",
  "terrain.dragToRemove": "Drag to remove",
  "terrain.dragToPaint": "Drag to paint",
  "terrain.plains": "Plains",
  "terrain.forest": "Forest",
  "terrain.hills": "Hills",
  "terrain.mountain": "Mountain",
  "terrain.water": "Water",
  "terrain.swamp": "Swamp",
  "terrain.desert": "Desert",
  "terrain.snow": "Snow",
  "terrain.settlement": "Settlement",

  // ---- annotation drawer -----------------------------------------------
  "annotation.title": "Labels & room numbers",
  "annotation.subtitle": "Set the content, then click where it should appear.",
  "annotation.text": "Text",
  "annotation.roomNumber": "Room #",
  "annotation.labelText": "Label text",
  "annotation.nextRoomNumber": "Next room number",
  "annotation.placeholder": "Secret passage, Trap, Altar…",
  "annotation.textSize": "Text size",
  "annotation.sizeSmall": "Small",
  "annotation.sizeMedium": "Medium",
  "annotation.sizeLarge": "Large",
  "annotation.sizeTitle": "Title",
  "annotation.color": "Color",
  "annotation.colorAria": "Annotation color",
  "annotation.keepPlacing": "Keep placing",
  "annotation.keepPlacingNumber": "Automatically advance the number",
  "annotation.keepPlacingText": "Place this label more than once",

  // ---- room keys (adventure keying) ------------------------------------
  "roomKey.title": "Room key",
  "roomKey.subtitle": "Room {number}",
  "roomKey.subtitleUnnumbered": "Unnumbered room",
  "roomKey.subtitleOrphan": "Orphaned key",
  "roomKey.close": "Close the room key",
  "roomKey.numberLabel": "Room number",
  "roomKey.numberHint": "Taken from the room number on the map",
  "roomKey.orphanNotice": "The room number this key belonged to has been deleted. The text is kept until you delete the key.",
  "roomKey.name": "Room name",
  "roomKey.namePlaceholder": "Collapsed guard post",
  "roomKey.description": "Description",
  "roomKey.descriptionPlaceholder": "What the room is, and what is in it",
  "roomKey.readAloud": "Read-aloud text",
  "roomKey.readAloudPlaceholder": "The passage you read out when the party walks in",
  "roomKey.senses": "Senses",
  "roomKey.sensesHint": "Optional detail beyond what the party can see",
  "roomKey.smell": "Smell",
  "roomKey.smellPlaceholder": "Damp stone",
  "roomKey.sound": "Sound",
  "roomKey.soundPlaceholder": "Water dripping somewhere behind the wall",
  "roomKey.temperature": "Temperature",
  "roomKey.temperaturePlaceholder": "Cold and still",
  "roomKey.texture": "Texture",
  "roomKey.texturePlaceholder": "Loose gravel underfoot",
  "roomKey.lighting": "Lighting",
  "roomKey.lightingPlaceholder": "Two guttering torches near the door",
  "roomKey.dmNotes": "DM notes",
  "roomKey.dmNotesPlaceholder": "Anything the players should not hear yet",
  "roomKey.dmOnly": "DM only",
  "roomKey.dmOnlyHint": "Marked DM-only. The player view that hides it arrives in a later phase.",
  "roomKey.saveHint": "Saved when you leave a field",
  "roomKey.orphanListTitle": "Orphaned room keys",
  "roomKey.orphanListHint": "Their room number was deleted. The text is kept here until you delete it.",
  "roomKey.orphanCount": "{count} orphaned keys",
  "roomKey.orphanCount_one": "{count} orphaned key",
  "roomKey.orphanCount_other": "{count} orphaned keys",
  "roomKey.untitled": "Untitled key",
  "roomKey.emptySnippet": "No text yet",
  "roomKey.openOrphan": "Open the orphaned key \"{name}\"",
  "roomKey.deleteOrphan": "Delete the orphaned key \"{name}\"",
  "roomKey.deleteOrphanConfirm": "Delete the orphaned room key \"{name}\"? Its text cannot be recovered.",
  "roomKey.deleted": "Room key deleted",

  // ---- export modal ----------------------------------------------------
  "export.title": "Print on A4",
  "export.subtitle": "Exact-scale, automatically tiled PDF.",
  "export.close": "Close export",
  "export.orientation": "Orientation",
  "export.portrait": "Portrait",
  "export.landscape": "Landscape",
  "export.quality": "Quality",
  "export.dpi300": "300 DPI · Print",
  "export.dpi150": "150 DPI · Draft",
  "export.printedHexWidth": "Printed hex width",
  "export.printedGridCell": "Printed grid cell",
  "export.mmInch": "25.4 mm · 1 inch",
  "export.mmHalfInch": "12.7 mm · ½ inch",
  "export.mmCompact": "10 mm · Compact",
  "export.pageOverlap": "Page overlap",
  "export.overlapNone": "None",
  "export.overlap5": "5 mm",
  "export.overlap10": "10 mm",
  "export.pages": "{count} A4 pages",
  "export.pages_one": "{count} A4 page",
  "export.pages_other": "{count} A4 pages",
  "export.pageGrid": "{columns} across × {rows} down",
  "export.exportPdf": "Export PDF",
  "export.tooManyPages": "This scale would create {count} pages. Reduce the print grid size or use landscape.",
  "export.pdfSubjectHex": "Tiled hexcrawl map",
  "export.pdfSubjectDungeon": "Tiled dungeon map",
  "export.pageFooter": "{title} · {column}/{columns}, {row}/{rows}",
  "export.canvasFailed": "Could not create the print canvas.",

  // ---- map library -----------------------------------------------------
  "library.title": "Map library",
  "library.count": "{count} saved maps · stored in this browser",
  "library.count_one": "{count} saved map · stored in this browser",
  "library.count_other": "{count} saved maps · stored in this browser",
  "library.close": "Close map library",
  "library.newMap": "New map",
  "library.empty": "No saved maps yet.",
  "library.newName": "New map name",
  "library.saveName": "Save name",
  "library.rename": "Rename {title}",
  "library.duplicate": "Duplicate {title}",
  "library.delete": "Delete {title}",
  "library.open": "Open",
  "library.deleteConfirm": "Delete \"{title}\"? This cannot be undone.",
  "library.justNow": "just now",
  "library.minutesAgo": "{count}m ago",
  "library.hoursAgo": "{count}h ago",
  "library.readFailed": "Could not read the map library",
  "library.actionFailed": "That map action failed",
  "library.opened": "Map opened",
  "library.openFailed": "That map could not be opened",
  "library.created": "Created \"{title}\"",
  "library.duplicated": "Duplicated as \"{title}\"",
  "library.deleted": "Deleted \"{title}\"",
  "library.duplicateFailed": "Could not duplicate that map",

  // ---- object quick actions (canvas overlay) ---------------------------
  "object.canvasDungeon": "Interactive dungeon map canvas",
  "object.canvasHex": "Interactive hexcrawl map canvas",
  "object.quickActions": "Quick actions for {label}",
  "object.selected": "{label} selected",
  "object.dragToMove": "Drag to move",
  "object.rotateLeft": "Rotate left 15 degrees",
  "object.rotateLeftTitle": "Rotate left 15° · [",
  "object.rotateRight": "Rotate right 15 degrees",
  "object.rotateRightTitle": "Rotate right 15° · ]",
  "object.duplicate": "Duplicate object",
  "object.duplicateTitle": "Duplicate · Ctrl/⌘ D",
  "object.lock": "Lock object",
  "object.unlock": "Unlock object",
  "object.delete": "Delete object",
  "object.deleteTitle": "Delete",
  "object.more": "More object actions",
  "object.moreTitle": "More actions",
  "object.smaller": "Smaller",
  "object.larger": "Larger",
  "object.makeSmaller": "Make object smaller",
  "object.makeLarger": "Make object larger",
  "object.back": "Back",
  "object.front": "Front",
  "object.sendBackward": "Send object backward",
  "object.bringForward": "Bring object forward",
  "object.content": "Content",
  "object.contentAria": "Annotation text",
  "object.size": "Size",
  "object.fontSizeAria": "Annotation font size",
  "object.widthAria": "Object width in grid cells",
  "object.heightAria": "Object height in grid cells",
  "object.cells": "cells",

  // ---- measurement -----------------------------------------------------
  "measure.hex": "hexes",
  "measure.hex_one": "hex",
  "measure.hex_other": "hexes",
  "measure.square": "squares",
  "measure.square_one": "square",
  "measure.square_other": "squares",
  "measure.readout": "{feet} ft · {count} {unit}",

  // ---- toasts ----------------------------------------------------------
  "toast.imported": "Imported \"{title}\" as a new map",
  "toast.importFailed": "Could not import the map",
  "toast.pngExported": "High-resolution PNG exported",
  "toast.pngFailed": "PNG export failed",
  "toast.pngNotReady": "The map is not ready to export.",
  "toast.pdfExported": "A4 print PDF exported successfully",
  "toast.pdfFailed": "PDF export failed",
  "toast.runMode": "Run mode: move tokens, measure distances, or pan the map · Esc exits",
  "toast.newMapFailed": "Could not create a new map",
  "toast.storageFailed": "Could not open local storage — changes will not be saved",

  // ---- document defaults ----------------------------------------------
  "doc.defaultTitle": "The Sunken Archive",
  "doc.untitledDungeon": "Untitled Dungeon",
  "doc.untitledHexcrawl": "Untitled Hexcrawl",
  "doc.defaultAnnotation": "Secret passage",
  "doc.labelFallback": "Label",
  "doc.copySuffix": "{title} (copy)",

  // ---- import validation ----------------------------------------------
  "validate.invalidFile": "Invalid map file.",
  "validate.unsupported": "This file is not a supported Dungeon Builder map.",

  // ---- canvas glyphs (drawn on the map, not layout) --------------------
  "glyph.tokenPlayer": "H",
  "glyph.tokenEnemy": "E",
  "glyph.tokenNpc": "N",
} as const;

export type TranslationKey = keyof typeof en;
export type Dictionary = Record<TranslationKey, string>;
